import React, {useContext, useEffect, useState} from 'react';
import {
    TextField,
    Button,
    Typography,
    Box,
    FormControlLabel,
    Checkbox,
    FormControl,
    Grid,
    Card,
    CardContent,
    CardActions, InputLabel, Select, MenuItem, Alert, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {GlobalStateContext} from "../state/GlobalStateProvider";
import {useAnimalAPI} from "../service/useAnimalAPI";
import {useLocation, useSearchParams} from "react-router-dom";
import PetsIcon from "@mui/icons-material/Pets";
import {withAuthenticationRequired} from "@auth0/auth0-react";
import Loading from "../components/common/Loading";
import {scrollToElementId} from "../utils/ScreenUtil";
import AppSnackbar from "../common/AppSnackbar";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useTranslation} from "react-i18next";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import nl from "date-fns/locale/nl";
import {getDateStringToAvoidTimezone} from "../common/DateUtil";
import {useTenantConfig} from "../security/tenant-config-context";

const animalModel = {
    name: '',
    birthDate: '',
    breed: '',
    sex: '',
    color: '',
    chipNumber: '',
    spayedNeutered: false,
    ontvlooitEnOntwormt: false,
    kattenziekte: false,
    niesziekte: false,
    kattenleukemie: false,
    // Optional dog fields (backwards compatible)
    dhp: false, // Distemper/Adenovirus/Parvo (DHP/DAP)
    leptospirose: false, // L4
    kennelCough: false, // Bordetella ± parainfluenza
    rabies: false, // optional per tenant
    // Dog medical extensions
    weightKg: null,
    sizeCategory: '', // XS/S/M/L/XL
    hasMedication: false,
    medicationNotes: '',
    allergies: '',
    femaleInHeat: null,
    lastHeatDate: '',
    medicalHistory: '',
    diet: '',
    behaviour: '',
    associationAnimals: '',
    contactOtherAnimals: false,
    // Dog behavior
    dogSocialization: 'UNKNOWN',
    reactiveOnLeash: null,
    resourceGuarding: null,
    separationAnxiety: null,
    houseTrained: null,
    biteHistory: null,
    biteHistoryNotes: '',
    muzzleRequired: null,
    okWithCats: null,
}

const AnimalInformationForm = ({updateCurrentStep, providedCat}) => {
    const { t } = useTranslation();
    const {getAnimals, updateAnimal, getAnimalByUuid, archiveAnimal} = useAnimalAPI();
    const location = useLocation();
    const {globalState, updateGlobalState} = useContext(GlobalStateContext);
    const { cfg } = useTenantConfig();
    const vaccinationsUrl = cfg.ui?.vaccinationsUrl;
    const isDogBusiness = cfg?.animalTypes?.includes('DOG');
    // Initialize with species type based on tenant business: DOG business → DOG, else CAT
    const [currentAnimal, setCurrentAnimal] = useState(() => ({
        ...animalModel,
        type: isDogBusiness ? 'DOG' : 'CAT',
    }));
    const [animals, setAnimals] = useState([]);
    const [errors, setErrors] = useState({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [searchParams] = useSearchParams();
    const [readOnly, setReadOnly] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [severity, setSeverity] = useState("error");
    const [isLoading, setIsLoading] = useState(true);
    const [openArchiveDialog, setOpenArchiveDialog] = useState(false);

    const isDog = (currentAnimal?.type || '').toUpperCase() === 'DOG' || (!currentAnimal?.type && isDogBusiness);
    function handleFetchError(reason) {
        setSnackbarMessage(reason)
        setOpenSnackbar(true)
    }

    useEffect(() => {
        if (providedCat) {
            setCurrentAnimal(providedCat)
        } else {
            setIsLoading(true);
            if (location.pathname !== "/mijn-katten") {
                updateGlobalState({catsForReservation: []})
            }

            if (searchParams.get('uuid')) {
                setReadOnly(true)
                getAnimalByUuid(searchParams.get('uuid'))
                    .then(value => {
                        if (value != null) {
                            setCurrentAnimal(value)
                            // setCurrentIndex(0)
                            // setCats(value)
                        } else {
                            throw {error: 'Geen katten gevonden'}; // should never happen
                        }
                    })
                    .catch(() => handleFetchError(t('fetchCatError')))
                    .finally(() => setIsLoading(false));
            } else if (globalState.reservationMeta.animalsInReservationIds.length === 0) {
                setIsLoading(true)

                getAnimals()
                    .then(value => {
                        if (value.length > 0) {
                            setCurrentAnimal(value[0])
                            setCurrentIndex(0)
                            setAnimals(value)
                        }
                    })
                    .catch(() => handleFetchError(t('fetchMyCatsError')))
                    .finally(() => setIsLoading(false));
            } else {
                getAnimals()
                    .then(value => {
                        if (value.length > 0) {
                            const catsForReservation = value.filter(value => globalState.reservationMeta.animalsInReservationIds.includes(value.id));
                            setAnimals(catsForReservation);
                            setCurrentAnimal(catsForReservation[0])
                            setCurrentIndex(0)
                        } else {
                            throw {error: 'Geen katten gevonden'}; // should never happen
                        }
                    })
                    .catch(() => handleFetchError(t('fetchMyCatsError')))
                    .finally(() => setIsLoading(false));
            }
        }
    }, []);

    const validateForm = () => {
        let tempErrors = {};
        tempErrors.name = currentAnimal.name ? "" : "Naam is een verplicht veld.";
        tempErrors.birthDate = currentAnimal.birthDate ? "" : "Geboortedatum is een verplicht veld.";
        tempErrors.breed = currentAnimal.breed ? "" : "Ras is een verplicht veld.";
        tempErrors.sex = currentAnimal.sex ? "" : "Geslacht is een verplicht veld.";
        tempErrors.color = currentAnimal.color ? "" : "Kleur is een verplicht veld.";
        if (!isDog) {
            tempErrors.behaviour = currentAnimal.behaviour ? "" : "Gedrag thuis is een verplicht veld.";
            tempErrors.associationAnimals = currentAnimal.associationAnimals ? "" : "Omgang met andere dieren is een verplicht veld.";
        }

        if (currentAnimal.chipNumber) {
            const chipNumberPattern = /^[0-9]{15}$/; // Example pattern for a 15-digit chip number
            tempErrors.chipNumber = chipNumberPattern.test(currentAnimal.chipNumber) ? "" : "Chipnummer niet geldig, een chipnummer heeft 15 cijfers.";
        }

        setErrors({...tempErrors});
        return Object.entries(tempErrors)
            .filter(([, value]) => value !== '')
            .map(([key]) => key);
    };

    const handleChange = (event) => {
        const {name, value, type, checked} = event.target;
        setCurrentAnimal(prevState => ({
            ...prevState, [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleCatOperation = successCallback => {
        setIsLoading(true)
        const invalidNameArray = validateForm();
        if (invalidNameArray.length === 0) {
            updateAnimal(currentAnimal)
                .then(value => {
                    successCallback(value);
                    setState({...state, open: true});
                })
                .catch(() => handleFetchError(t('updateMyCatsError')))
                .finally(() => setIsLoading(false));
        } else {
            scrollToElementId(invalidNameArray[0]);
            setIsLoading(false)
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        handleCatOperation(() => updateCurrentStep(6));
    };

    const updateCurrentCat = () => handleCatOperation(value => setCurrentAnimal(value));

    const handleArchive = () => {
        if (!currentAnimal?.uuid) {
            handleFetchError(t('archiveCatFailed'));
            return;
        }
        setIsLoading(true);
        archiveAnimal(currentAnimal.uuid)
            .then((updated) => {
                setOpenArchiveDialog(false);
                if (updated) {
                    setCurrentAnimal(updated);
                }
                setSeverity("success");
                setSnackbarMessage(t('archiveCatSuccess'));
                setOpenSnackbar(true);
                getAnimals()
                    .then(value => {
                        if (value.length > 0) {
                            setCurrentAnimal(value[0])
                            setCurrentIndex(0)
                            setAnimals(value)
                        } else {
                            // Refresh to the site's base URL (protocol + host)
                            window.location.href = window.location.origin;
                        }
                    })
                    .catch(() => handleFetchError(t('fetchMyCatsError')))
                    .finally(() => setIsLoading(false));
            })
            .catch(() => handleFetchError(t('archiveCatFailed')))
            .finally(() => setIsLoading(false));
    };

    const changeCat = index => {
        handleCatOperation(() => {
            setCurrentAnimal(animals[index]);
            setCurrentIndex(currentIndex + 1); // TODO always increment looks wrong, get the index via the button
        });
    };

    const goBack = () => updateCurrentStep(4);

    const styles = {
        optionContainer: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: "center",
            alignItems: 'center',
            gap: '20px',
            marginBottom: '20px'
        },
        optionItem: {
            display: 'flex',
            alignItems: 'center',
        },
    };

    const [state, setState] = React.useState({open: false, vertical: 'top', horizontal: 'center'});
    const parseDate = (dateString) => new Date(dateString);
    return (
        <>
            {isLoading && (<Loading />)}
            {!isLoading && (
                <Grid container alignItems="center" justifyContent="center" spacing={1} sx={{ textAlign: 'center', mt:1 }}>
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        <Typography variant="h5" color="secondary" gutterBottom>
                            <div style={styles.optionContainer}>
                                {animals.map((animal, index) => (
                                    <div key={index} style={styles.optionItem}>
                                        <Button color="primary" onClick={() => changeCat(index)} disabled={isLoading} data-testid={`animal-tab-${animal.name}`}>
                                            <PetsIcon /> {animal.name}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                            <Card variant="outlined" style={{ marginBottom: '10px' }}>
                                <CardContent>
                                    <Typography variant="h5" color="secondary">
                                        {t("catInfo", { name: currentAnimal.name })}
                                    </Typography>
                                    <TextField disabled margin="normal" fullWidth id="name" label={t("name")} name="name" value={currentAnimal.name} onChange={handleChange}
                                               error={!!errors.name}
                                               helperText={errors.name}
                                               inputProps={{ "data-testid": "animal-name" }} />
                                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                                        <DatePicker
                                            slotProps={{
                                                textField: {
                                                    id: "birthDate",
                                                    fullWidth: true,
                                                    required: true,
                                                    error: !!errors.birthDate,
                                                    helperText: errors.birthDate,
                                                    inputProps: { "data-testid": "animal-birthdate" }
                                                },
                                            }}
                                            value={currentAnimal.birthDate ? parseDate(currentAnimal.birthDate) : null}
                                            label={t("birthDate")}
                                            onChange={(d) => {
                                                currentAnimal.birthDate = d ? getDateStringToAvoidTimezone(d) : null; // store date-only
                                            }}
                                            inputFormat="dd-MM-yyyy"
                                            disabled={readOnly}
                                            error={!!errors.birthDate}
                                            helperText={errors.birthDate}/>
                                    </LocalizationProvider>
                                    <TextField margin="normal" required fullWidth id="breed" label={t("breed")} name="breed" value={currentAnimal.breed || ''} onChange={handleChange}
                                               error={!!errors.breed}
                                               disabled={readOnly}
                                               helperText={errors.breed}
                                               inputProps={{ "data-testid": "animal-breed" }} />

                                    <FormControl fullWidth>
                                        <InputLabel id="sex-select-label">{t("sex")} *</InputLabel>
                                        <Select
                                            required
                                            align="left"
                                            labelId="sex-select-label"
                                            id="sex-select"
                                            name="sex"
                                            disabled={readOnly}
                                            value={currentAnimal.sex}
                                            onChange={handleChange}
                                            label={t("sex")}
                                            inputProps={{ "data-testid": "animal-sex-select" }}
                                        >
                                            <MenuItem value='Male' data-testid="animal-sex-male">{t("male")}</MenuItem>
                                            <MenuItem value='Female' data-testid="animal-sex-female">{t("female")}</MenuItem>
                                            <MenuItem value='Unknown' data-testid="animal-sex-unknown">{t("unknown")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField margin="normal" required fullWidth id="color" label={t("color")} name="color" value={currentAnimal.color || ''} onChange={handleChange}
                                               disabled={readOnly}
                                               error={!!errors.color}
                                               helperText={errors.color}
                                               inputProps={{ "data-testid": "animal-color" }} />
                                    <TextField margin="normal" fullWidth id="chipNumber" label={t("chipNumber")} name="chipNumber" value={currentAnimal.chipNumber || ''} onChange={handleChange}
                                               disabled={readOnly}
                                               error={!!errors.chipNumber}
                                               helperText={errors.chipNumber}
                                               inputProps={{ "data-testid": "animal-chip-number" }} />
                                </CardContent>
                            </Card>
                            <Card variant="outlined" style={{ marginBottom: '10px' }}>
                                <CardContent>
                                    <Typography component="h1" variant="h5">
                                        {t("medicalInfo", { name: currentAnimal.name })}
                                    </Typography>
                                    <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                        <Tooltip title={t("mandatoryNeutering")}>
                                            <Box display="inline-flex" alignItems="center">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={currentAnimal.spayedNeutered}
                                                        onChange={handleChange}
                                                        disabled={readOnly}
                                                        name="spayedNeutered"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-spayed-neutered" }}
                                                    />}
                                                    label={t("neutered")}
                                                />
                                                <InfoOutlinedIcon sx={{ height: 15, marginLeft: -2 }} />
                                            </Box>
                                        </Tooltip>
                                    </FormControl>
                                    <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                        <FormControlLabel
                                            control={<Checkbox
                                                checked={currentAnimal.ontvlooitEnOntwormt}
                                                onChange={handleChange}
                                                disabled={readOnly}
                                                name="ontvlooitEnOntwormt"
                                                color="primary"
                                                inputProps={{ "data-testid": "animal-dewormed" }}
                                            />}
                                            label={t('dewormed')}
                                        />
                                    </FormControl>
                                    {isDog && (
                                        <>
                                            {/* Dog-only medical extensions */}
                                            <TextField
                                                margin="normal"
                                                fullWidth
                                                id="weightKg"
                                                label={t('dog_medical_weightKg')}
                                                name="weightKg"
                                                type="number"
                                                inputProps={{ step: 0.1, min: 0, max: 200, "data-testid": "animal-weight" }}
                                                disabled={readOnly}
                                                value={currentAnimal.weightKg ?? ''}
                                                onChange={handleChange}
                                            />
                                            <FormControl fullWidth sx={{ mt: 2 }}>
                                                <InputLabel id="sizeCategory-label">{t('dog_medical_sizeCategory')}</InputLabel>
                                                <Select
                                                    labelId="sizeCategory-label"
                                                    id="sizeCategory"
                                                    name="sizeCategory"
                                                    disabled={readOnly}
                                                    value={currentAnimal.sizeCategory || ''}
                                                    label={t('dog_medical_sizeCategory')}
                                                    onChange={handleChange}
                                                    inputProps={{ "data-testid": "animal-size-category" }}
                                                >
                                                    <MenuItem value='XS' data-testid="animal-size-xs">{t('dog_size_xs')}</MenuItem>
                                                    <MenuItem value='S' data-testid="animal-size-s">{t('dog_size_s')}</MenuItem>
                                                    <MenuItem value='M' data-testid="animal-size-m">{t('dog_size_m')}</MenuItem>
                                                    <MenuItem value='L' data-testid="animal-size-l">{t('dog_size_l')}</MenuItem>
                                                    <MenuItem value='XL' data-testid="animal-size-xl">{t('dog_size_xl')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.hasMedication}
                                                        onChange={handleChange}
                                                        disabled={readOnly}
                                                        name="hasMedication"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-has-medication" }}
                                                    />}
                                                    label={t('dog_medical_hasMedication')}
                                                />
                                            </FormControl>
                                            {currentAnimal.hasMedication && (
                                                <TextField
                                                    margin="normal"
                                                    fullWidth
                                                    id="medicationNotes"
                                                    label={t('dog_medical_medicationNotes')}
                                                    name="medicationNotes"
                                                    multiline
                                                    rows={3}
                                                    disabled={readOnly}
                                                    value={currentAnimal.medicationNotes || ''}
                                                    onChange={handleChange}
                                                    inputProps={{ "data-testid": "animal-medication-notes" }}
                                                />
                                            )}
                                            <TextField
                                                margin="normal"
                                                fullWidth
                                                id="allergies"
                                                label={t('dog_medical_allergies')}
                                                name="allergies"
                                                multiline
                                                rows={3}
                                                disabled={readOnly}
                                                value={currentAnimal.allergies || ''}
                                                onChange={handleChange}
                                                inputProps={{ "data-testid": "animal-allergies" }}
                                            />
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={currentAnimal.femaleInHeat ?? false}
                                                        onChange={handleChange}
                                                        disabled={readOnly}
                                                        name="femaleInHeat"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-female-in-heat" }}
                                                    />}
                                                    label={t('dog_medical_femaleInHeat')}
                                                />
                                            </FormControl>
                                            {currentAnimal.femaleInHeat && (
                                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                                                    <DatePicker
                                                        slotProps={{
                                                            textField: {
                                                                id: 'lastHeatDate',
                                                                fullWidth: true,
                                                                inputProps: { "data-testid": "animal-last-heat-date" }
                                                            },
                                                        }}
                                                        value={currentAnimal.lastHeatDate ? parseDate(currentAnimal.lastHeatDate) : null}
                                                        label={t('dog_medical_lastHeatDate')}
                                                        onChange={(d) => {
                                                            currentAnimal.lastHeatDate = d ? getDateStringToAvoidTimezone(d) : null;
                                                        }}
                                                        inputFormat="dd-MM-yyyy"
                                                        disabled={readOnly}
                                                    />
                                                </LocalizationProvider>
                                            )}
                                        </>
                                    )}
                                    {!isDog && (
                                        <>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                {/* Tooltip text uses tenant-aware translation */}
                                                <Tooltip title={t('vaccinationRequiredTooltip', { tenantName: cfg?.name })}>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={currentAnimal.kattenleukemie}
                                                                onChange={handleChange}
                                                                disabled={readOnly}
                                                                name="kattenleukemie"
                                                                color="primary"
                                                                inputProps={{ "data-testid": "animal-vaccination-leukemia" }}
                                                            />}
                                                            label={t('leukemia')}
                                                        />
                                                        <InfoOutlinedIcon sx={{height: 15, marginLeft: -2}}/>
                                                    </Box>
                                                </Tooltip>
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <Tooltip title={t('vaccinationRequiredTooltip', { tenantName: cfg?.name })}>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={currentAnimal.kattenziekte}
                                                                onChange={handleChange}
                                                                disabled={readOnly}
                                                                name="kattenziekte"
                                                                color="primary"
                                                                inputProps={{ "data-testid": "animal-vaccination-panleukopenia" }}
                                                            />}
                                                            label={t('panleukopenia')}
                                                        />
                                                        <InfoOutlinedIcon sx={{height: 15, marginLeft: -2}}/>
                                                    </Box>
                                                </Tooltip>
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <Tooltip title={t('vaccinationRequiredTooltip', { tenantName: cfg?.name })}>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={currentAnimal.niesziekte}
                                                                onChange={handleChange}
                                                                disabled={readOnly}
                                                                name="niesziekte"
                                                                color="primary"
                                                                inputProps={{ "data-testid": "animal-vaccination-coryza" }}
                                                            />}
                                                            label={t('coryza')}
                                                        />
                                                        <InfoOutlinedIcon sx={{height: 15, marginLeft: -2}}/>
                                                    </Box>
                                                </Tooltip>
                                            </FormControl>
                                            <a href={vaccinationsUrl} target="_blank" style={{ float: "left", marginBottom: '25px' }} data-testid="check-vaccinations-link">
                                                {t("checkCatVaccinations")}
                                            </a>
                                        </>
                                    )}
                                    {isDog && (
                                        <>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <Tooltip title={t('vaccinationRequiredTooltip', { tenantName: cfg?.name })}>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={currentAnimal.dhp}
                                                                onChange={handleChange}
                                                                disabled={readOnly}
                                                                name="dhp"
                                                                color="primary"
                                                                inputProps={{ "data-testid": "animal-vaccination-dhp" }}
                                                            />}
                                                            label={t('dog_vaccination_dhp')}
                                                        />
                                                        <InfoOutlinedIcon sx={{height: 15, marginLeft: -2}}/>
                                                    </Box>
                                                </Tooltip>
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <Tooltip title={t('vaccinationRequiredTooltip', { tenantName: cfg?.name })}>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={currentAnimal.leptospirose}
                                                                onChange={handleChange}
                                                                disabled={readOnly}
                                                                name="leptospirose"
                                                                color="primary"
                                                                inputProps={{ "data-testid": "animal-vaccination-lepto" }}
                                                            />}
                                                            label={t('dog_vaccination_lepto')}
                                                        />
                                                        <InfoOutlinedIcon sx={{height: 15, marginLeft: -2}}/>
                                                    </Box>
                                                </Tooltip>
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <Tooltip title={t('vaccinationRequiredTooltip', { tenantName: cfg?.name })}>
                                                    <Box display="inline-flex" alignItems="center">
                                                        <FormControlLabel
                                                            control={<Checkbox
                                                                checked={currentAnimal.kennelCough}
                                                                onChange={handleChange}
                                                                disabled={readOnly}
                                                                name="kennelCough"
                                                                color="primary"
                                                                inputProps={{ "data-testid": "animal-vaccination-kennel-cough" }}
                                                            />}
                                                            label={t('dog_vaccination_kennel_cough')}
                                                        />
                                                        <InfoOutlinedIcon sx={{height: 15, marginLeft: -2}}/>
                                                    </Box>
                                                </Tooltip>
                                            </FormControl>
                                            {/* Rabies optional/tenant setting: show as optional without tooltip requirement */}
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <Box display="inline-flex" alignItems="center">
                                                    <FormControlLabel
                                                        control={<Checkbox
                                                            checked={currentAnimal.rabies}
                                                            onChange={handleChange}
                                                            disabled={readOnly}
                                                            name="rabies"
                                                            color="primary"
                                                            inputProps={{ "data-testid": "animal-vaccination-rabies" }}
                                                        />}
                                                        label={t('dog_vaccination_rabies_optional')}
                                                    />
                                                </Box>
                                            </FormControl>
                                            <a href={vaccinationsUrl} target="_blank" style={{ float: "left", marginBottom: '25px' }} data-testid="check-vaccinations-link">
                                                {t("checkDogVaccinations")}
                                            </a>
                                        </>
                                    )}
                                    {/* Health Information */}
                                    <TextField margin="normal" fullWidth id="diet" label={t("diet")} name="diet" multiline rows={4} disabled={readOnly}
                                               value={currentAnimal.diet || ''} onChange={handleChange} error={!!errors.diet} helperText={errors.diet}
                                               inputProps={{ "data-testid": "animal-diet" }} />
                                    <TextField margin="normal" fullWidth id="medicalHistory" label={t("medicalHistory")} name="medicalHistory" multiline rows={4} disabled={readOnly}
                                               value={currentAnimal.medicalHistory || ''}
                                               onChange={handleChange} error={!!errors.medicalHistory} helperText={errors.medicalHistory}
                                               inputProps={{ "data-testid": "animal-medical-history" }} />
                                </CardContent>
                            </Card>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography component="h1" variant="h5">
                                        {t("behavior", { name: currentAnimal.name })}
                                    </Typography>
                                    {!isDog && (
                                        <>
                                            <TextField inputProps={{ maxLength: 300, "data-testid": "animal-behaviour" }} margin="normal" required fullWidth id="behaviour" label={t("homeBehavior")} name="behaviour" multiline rows={4} disabled={readOnly}
                                                       value={currentAnimal.behaviour || ''} onChange={handleChange} error={!!errors.behaviour} helperText={errors.behaviour} />
                                            <TextField inputProps={{ maxLength: 300, "data-testid": "animal-association-animals" }} margin="normal" required fullWidth id="associationAnimals" label={t("associationAnimals")} name="associationAnimals" multiline
                                                       rows={4} disabled={readOnly}
                                                       value={currentAnimal.associationAnimals || ''} onChange={handleChange} error={!!errors.associationAnimals}
                                                       helperText={errors.associationAnimals} />
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={currentAnimal?.contactOtherAnimals}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="contactOtherAnimals"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-contact-other-animals" }}
                                                    />}
                                                    label={t("allowContact")}
                                                />
                                            </FormControl>
                                        </>
                                    )}
                                    {isDog && (
                                        <>
                                            <FormControl fullWidth>
                                                <InputLabel id="dog-socialization-label">{t('dog_behavior_socialization')}</InputLabel>
                                                <Select
                                                    labelId="dog-socialization-label"
                                                    id="dogSocialization"
                                                    name="dogSocialization"
                                                    disabled={readOnly}
                                                    value={currentAnimal.dogSocialization || 'UNKNOWN'}
                                                    label={t('dog_behavior_socialization')}
                                                    onChange={handleChange}
                                                    inputProps={{ "data-testid": "animal-dog-socialization" }}
                                                >
                                                    <MenuItem value='GROUP_OK' data-testid="animal-socialization-group-ok">{t('dog_socialization_group_ok')}</MenuItem>
                                                    <MenuItem value='SMALL_GROUP_ONLY' data-testid="animal-socialization-small-group-only">{t('dog_socialization_small_group_only')}</MenuItem>
                                                    <MenuItem value='SOLO_ONLY' data-testid="animal-socialization-solo-only">{t('dog_socialization_solo_only')}</MenuItem>
                                                    <MenuItem value='UNKNOWN' data-testid="animal-socialization-unknown">{t('dog_socialization_unknown')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.reactiveOnLeash}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="reactiveOnLeash"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-reactive-on-leash" }}
                                                    />}
                                                    label={t('dog_behavior_reactiveOnLeash')}
                                                />
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.resourceGuarding}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="resourceGuarding"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-resource-guarding" }}
                                                    />}
                                                    label={t('dog_behavior_resourceGuarding')}
                                                />
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.separationAnxiety}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="separationAnxiety"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-separation-anxiety" }}
                                                    />}
                                                    label={t('dog_behavior_separationAnxiety')}
                                                />
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.houseTrained}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="houseTrained"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-house-trained" }}
                                                    />}
                                                    label={t('dog_behavior_houseTrained')}
                                                />
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.biteHistory}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="biteHistory"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-bite-history" }}
                                                    />}
                                                    label={t('dog_behavior_biteHistory')}
                                                />
                                            </FormControl>
                                            {currentAnimal.biteHistory && (
                                                <TextField
                                                    margin="normal"
                                                    fullWidth
                                                    id="biteHistoryNotes"
                                                    label={t('dog_behavior_biteHistoryNotes')}
                                                    name="biteHistoryNotes"
                                                    multiline
                                                    rows={3}
                                                    disabled={readOnly}
                                                    value={currentAnimal.biteHistoryNotes || ''}
                                                    onChange={handleChange}
                                                    inputProps={{ "data-testid": "animal-bite-history-notes" }}
                                                />
                                            )}
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.muzzleRequired}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="muzzleRequired"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-muzzle-required" }}
                                                    />}
                                                    label={t('dog_behavior_muzzleRequired')}
                                                />
                                            </FormControl>
                                            <FormControl component="fieldset" variant="standard" fullWidth margin="normal">
                                                <FormControlLabel
                                                    control={<Checkbox
                                                        checked={!!currentAnimal.okWithCats}
                                                        disabled={readOnly}
                                                        onChange={handleChange}
                                                        name="okWithCats"
                                                        color="primary"
                                                        inputProps={{ "data-testid": "animal-ok-with-cats" }}
                                                    />}
                                                    label={t('dog_behavior_okWithCats')}
                                                />
                                            </FormControl>
                                        </>
                                    )}
                                    {!isLoading && (!isDog && (!currentAnimal.kattenziekte || !currentAnimal.kattenleukemie || !currentAnimal.niesziekte)) && (
                                        <Alert sx={{ marginTop: 2 }} severity="warning" data-testid="vaccination-warning">
                                            {t("vaccinationWarning", { tenantName: cfg?.name })}
                                        </Alert>
                                    )}
                                    {!isLoading && (isDog && (!currentAnimal.dhp || !currentAnimal.leptospirose || !currentAnimal.kennelCough)) && (
                                        <Alert sx={{ marginTop: 2 }} severity="warning" data-testid="vaccination-warning">
                                            {t("vaccinationWarning", { tenantName: cfg?.name })}
                                        </Alert>
                                    )}
                                </CardContent>
                                <CardActions style={{
                                    display: 'flex', justifyContent: location.pathname !== "/mijn-profiel" ? 'space-between' : 'right'
                                }}>
                                    {location.pathname !== "/mijn-katten" && (
                                        <>
                                            <Button onClick={goBack} variant="contained" disabled={isLoading} data-testid="previous-button">
                                                {t("previous")}
                                            </Button>
                                            {currentIndex + 1 === animals.length && (
                                                <Button type="submit" variant="contained" disabled={isLoading} data-testid="next-button">
                                                    {t("next")}
                                                </Button>
                                            )}
                                            {currentIndex + 1 < animals.length && (
                                                <Button variant="contained" onClick={() => changeCat(1)} disabled={isLoading} data-testid="fill-next-animal-button">
                                                    {t("fillData", { name: animals[currentIndex + 1].name })}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    {!readOnly && location.pathname === "/mijn-katten" && (
                                        <>
                                            <Button onClick={() => setOpenArchiveDialog(true)} variant="outlined" color="error" disabled={isLoading || !currentAnimal?.uuid} data-testid="archive-animal-button">
                                                {t('archiveAnimal')}
                                            </Button>
                                            <Button onClick={updateCurrentCat} variant="contained" disabled={isLoading} data-testid="update-animal-button">
                                                {t("update")}
                                            </Button>
                                        </>
                                    )}
                                </CardActions>
                            </Card>
                        </Box>
                    </Grid>
                    <AppSnackbar openSnackbar={openSnackbar} snackbarMessage={snackbarMessage} severity={severity} setOpenSnackbar={setOpenSnackbar} />
                    <Dialog open={openArchiveDialog} onClose={() => setOpenArchiveDialog(false)}>
                        <DialogTitle>{t('archiveCatConfirmTitle')}</DialogTitle>
                        <DialogContent>
                            <Typography>{t('archiveCatConfirmMessage', { name: currentAnimal?.name })}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenArchiveDialog(false)} disabled={isLoading}>{t('cancel')}</Button>
                            <Button onClick={handleArchive} color="error" variant="contained" disabled={isLoading}>{t('archiveAnimal')}</Button>
                        </DialogActions>
                    </Dialog>
                </Grid>)}
        </>
    )
};


export default withAuthenticationRequired(AnimalInformationForm, {
    onRedirecting: () => <Loading/>,
    returnTo:
        '/nieuwe-reservatie'
});
